import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { ReviewableListProps } from '../../hooks';
import { card_container_style, card_section_style } from '../styles';
import { Button } from 'components/base/Button';
import Image from 'components/base/Image';
import Text from 'components/base/Text';
import { useUserInfo } from 'components/my-page/hooks/useUserInfo';
import { SAVED_WRITE_REVIEW_KEY } from 'components/reviews/store/write';
import { useBottomDialog } from 'context/ButtomDialogContext';
import { gray100, pink_primary, gray300 } from 'styles/colors';
import { useTracker } from 'util/Tracker';

enum CONFIRM_ID {
  CANCEL = 'CANCEL',
  TO_INQURY = 'TO_INQURY',
}

interface Props {
  item_info: ReviewableListProps;
}

const common_ubl = {
  navigation: 'my_review',
  object_type: 'button',
  object_section: 'tab_writable_review',
};

/**
 * 작성 가능한 리뷰 카드 컴포넌트
 * @component
 */
const ReviewableCard: FC<Props> = ({ item_info }) => {
  const router = useRouter();
  const tracker = useTracker();

  const { product_info, review_info, quantity, order_item_number } = item_info;
  const { user_info } = useUserInfo();
  const { open: openDialog } = useBottomDialog();
  const [is_saved_review, setIsSavedReview] = useState<boolean>(false);

  useEffect(() => {
    const saved_review_list = JSON.parse(localStorage.getItem(SAVED_WRITE_REVIEW_KEY) ?? '[]');
    // @ts-ignore
    const is_saved = saved_review_list.some((list) => list.order_item_number === item_info.order_item_number);

    setIsSavedReview(is_saved);
  }, [item_info.order_item_number]);

  const deadline = dayjs(review_info.date_deadline).diff(dayjs(), 'day');
  const deadline_text = deadline > 0 ? `작성 ${deadline}일 남음` : '오늘까지 작성 가능';

  const openLmitInfoDialog = async () => {
    const id = await openDialog({
      contents: {
        title: '리뷰를 작성할 수 없어요.',
        text: '리뷰 작성이 제한된 계정이에요. \n 자세한 내용은 마이페이지의 앱 문의/건의를 이용해주세요.',
      },
      buttons: [
        { id: CONFIRM_ID.CANCEL, variant: 'tertiary_gray', children: '취소', size: 'large' },
        { id: CONFIRM_ID.TO_INQURY, children: '문의하기', size: 'large' },
      ],
    });

    return id;
  };

  const handleClickWriteReview = async () => {
    const is_review_limited = user_info.product_review_info?.restriction;

    if (is_review_limited) {
      const result_id = await openLmitInfoDialog();

      if (result_id === CONFIRM_ID.TO_INQURY) {
        router.push('/my-page/inquiry');
      }
      return;
    }

    tracker.addLog({
      ...common_ubl,
      category: 'click',
      object_id: is_saved_review ? 'continuew_writing_review' : 'witer_review',
    });

    router.push(`/reviews/write?order_item_number=${order_item_number}`);
  };

  return (
    <SC.Container>
      <div className='thumbnail'>
        <Image
          src={product_info.image_url}
          layout='fill'
          objectFit='cover'
          alt={`작성가능한 리뷰 리스트의 상품 ${product_info.name} 이미지`}
        />
      </div>
      <div css={card_section_style}>
        <Text variant='body3' as='p' className='product_name reviewable'>
          {product_info.name}
        </Text>
        <Text variant='small1_regular' as='p' color='gray500' className='product_options reviewable'>
          {`${product_info.options} ${quantity}개`}
        </Text>
        <SC.ReviewInfo>
          {review_info.max_point > 0 && (
            <Text variant='body4_medium' color='gray_secondary' className='point'>
              최대 {review_info.max_point}P 적립 가능
            </Text>
          )}
          <Text variant='body4_medium' color='gray_secondary' className={cx({ primary_color: deadline < 16 })}>
            {deadline_text}
          </Text>
        </SC.ReviewInfo>
        <SC.ReviewWriteButton variant='tertiary_gray' size='small' onClick={handleClickWriteReview}>
          {is_saved_review ? '이어서 작성하기' : '리뷰 작성하기'}
        </SC.ReviewWriteButton>
      </div>
    </SC.Container>
  );
};

export default ReviewableCard;

const SC = {
  Container: styled.div`
    ${card_container_style};
    padding: 16px;

    &:not(:first-of-type) {
      border-top: 8px solid ${gray100};
    }
  `,
  ReviewInfo: styled.div`
    display: flex;
    align-items: center;

    .point {
      display: flex;
      align-items: center;

      &:after {
        content: '';
        background: ${gray300};
        width: 3px;
        height: 3px;
        margin: 0 4px;
        border-radius: 3px;
        display: inline-block;
      }
    }
    .primary_color {
      color: ${pink_primary};
    }
  `,
  ReviewWriteButton: styled(Button)`
    margin-top: 14px;
    height: 36px;
  `,
};
